import React, { useMemo } from 'react';

import { MarkRuleContext } from '../../utils/dashboardMarkRule';

interface DashboardMarkLegendProps {
    ctx: MarkRuleContext;
    /** fieldName → 图例中显示的简短指标名，如 { inHubNoUpdateRate: '仓内断更' } */
    metricLabels: Record<string, string>;
}

/** 把周环比阈值格式化为 `+0.05pp` / `-5pp` */
function formatWomThreshold(womValue: number): string {
    return `${womValue > 0 ? '+' : ''}${womValue}pp`;
}

/**
 * 看板异常标记图例。
 *
 * 阈值文案由后端配置推导，不二次硬编码——否则改了配置后图例会与实际标记不一致。
 * 无生效规则时不渲染，避免在周/月粒度下显示一条无意义的说明。
 */
const DashboardMarkLegend: React.FC<DashboardMarkLegendProps> = ({ ctx, metricLabels }) => {
    const { targetDesc, womDesc } = useMemo(() => {
        const rules = Array.from(ctx.ruleByField.values());
        if (rules.length === 0) {
            return { targetDesc: '', womDesc: '' };
        }

        const target = rules
            .map((rule) => {
                const label = metricLabels[rule.fieldName] ?? rule.fieldName;
                return `${label}${rule.targetOperator}${rule.targetValue}%`;
            })
            .join('，');

        // 多数看板各指标共用同一个周环比阈值，此时合并成一句；
        // 阈值不同则按阈值分组，同阈值的指标名用 ` / ` 并列，避免同一个数字重复出现多次
        const labelsByThreshold = new Map<string, string[]>();
        rules.forEach((rule) => {
            // 未配周环比的指标不标红框，不计入红框图例
            if (rule.womValue == null) return;
            const threshold = formatWomThreshold(rule.womValue);
            const label = metricLabels[rule.fieldName] ?? rule.fieldName;
            const labels = labelsByThreshold.get(threshold);
            if (labels) {
                labels.push(label);
            } else {
                labelsByThreshold.set(threshold, [label]);
            }
        });
        const wom =
            labelsByThreshold.size === 1
                ? `周环比${Array.from(labelsByThreshold.keys())[0]}`
                : Array.from(labelsByThreshold.entries())
                      .map(([threshold, labels]) => `${labels.join(' / ')}周环比${threshold}`)
                      .join('，');

        return { targetDesc: target, womDesc: wom };
    }, [ctx, metricLabels]);

    if (!ctx.enabled || ctx.ruleByField.size === 0) {
        return null;
    }

    return (
        <div className="mb-3 flex items-center gap-6 flex-wrap text-sm">
            <span className="flex items-center gap-2">
                <span
                    className="inline-block w-4 h-4 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: '#fff3cd' }}
                />
                <span>
                    <span className="font-bold">黄色底色加粗</span>：日会需关注指标
                </span>
            </span>
            <span>
                <span className="dashboard-mark-text-danger">红色数字</span>：未达目标（{targetDesc}）
            </span>
            {womDesc && (
                <span className="flex items-center gap-2">
                    <span
                        className="inline-block w-4 h-4 rounded-sm flex-shrink-0"
                        style={{ boxShadow: 'inset 0 0 0 1px #ff4d4f' }}
                    />
                    <span>红色边框：{womDesc}</span>
                </span>
            )}
        </div>
    );
};

export default DashboardMarkLegend;
